
import axios from "axios";
import moment from "moment";
import base_url from "../../api/bootapi";



export const fetchAirlines = async (inputValue, search_by = "airline_name") => {
    if (!inputValue || inputValue.length === 0) return [];

    try {
        const response = await axios.get(
            `${base_url.api1}/tourapp/flight_states_airlines?${search_by}=${inputValue}`
        );

        if (!response.data || !response.data.Airlines) return [];

        const airlines = response.data.Airlines.map((airline) => ({
            value: airline.id,
            label: `${airline.airline_name} ${airline.iata_code ? `(${airline.iata_code})` : ""}`,
            airline_name: airline?.airline_name || "",
            iata_code: airline?.iata_code || "",
            hub_code: airline.icao_code,
        }));

        const search = inputValue.toLowerCase();

        // 1️⃣ EXACT MATCH
        const exact = airlines.filter((a) =>
            a.iata_code.toLowerCase() === search || a.airline_name.toLowerCase() === search
        );

        // 2️⃣ STARTS WITH
        const startsWith = airlines.filter((a) =>
            (a.iata_code.toLowerCase().startsWith(search) || a.airline_name.toLowerCase().startsWith(search)) &&
            !(a.iata_code.toLowerCase() === search || a.airline_name.toLowerCase() === search)
        );

        // 3️⃣ CONTAINS ANYWHERE
        const contains = airlines.filter((a) =>
            (a.iata_code.toLowerCase().includes(search) || a.airline_name.toLowerCase().includes(search)) &&
            !(a.iata_code.toLowerCase().startsWith(search) || a.airline_name.toLowerCase().startsWith(search))
        );

        return [...exact, ...startsWith, ...contains];
    } catch (error) {
        console.error("Error fetching airlines:", error);
        return [];
    }
};

export const fetchAirports = async (inputValue) => {
    if (!inputValue || inputValue.length === 0) return [];

    try {
        const response = await axios.get(
            `${base_url.api1}/tourapp/flight_states_airports?search=${inputValue}&sortby=airport_name`
        );

        if (response.data && response.data.Airports) {
            return response.data.Airports.map((airport) => ({
                value: airport.id,
                label: `${airport.airport_name.replace(/[\n\u0000-\u001F\u007F-\u009F]/g, "")} (${airport.iata_code})`,
            }));
        }
        return [];
    } catch (error) {
        console.error("Error fetching airports:", error);
        return [];
    }
};

// Optimized to fetch both initial lists concurrently
export const fetchInitialFlightData = async () => {
    try {
        const [airlineResponse, airportResponse] = await Promise.all([
            axios.get(`${base_url.api1}/tourapp/flight_states_airlines?sortby=airline_name`),
            axios.get(`${base_url.api1}/tourapp/flight_states_airports?sortby=airport_name`)
        ]);

        let initialAirlines = [];
        let initialAirports = [];

        if (airlineResponse.data && airlineResponse.data.Airlines) {
            initialAirlines = airlineResponse.data.Airlines.map((airline) => ({
                value: airline.iata_code,
                label: `${airline.airline_name} ${airline.iata_code ? `(${airline.iata_code})` : ""}`,
                hub_code: airline.iata_code, // Note: You mapped hub_code to icao_code in fetchAirlines, ensure this is correct
            }));
        }

        if (airportResponse.data && airportResponse.data.Airports) {
            initialAirports = airportResponse.data.Airports.map((airport) => ({
                value: airport.id,
                label: `${airport.airport_name.replace(/[\n\u0000-\u001F\u007F-\u009F]/g, "")} (${airport.iata_code})`,
            }));
        }

        return { initialAirlines, initialAirports };
    } catch (error) {
        console.error("Error fetching initial flight data:", error);
        return { initialAirlines: [], initialAirports: [] };
    }
};

export const dateFormat = (dateString) => {
    if (!dateString) return "";

    try {

        const dateStr = dateString.slice(0, 16);

        const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

        if (!regex.test(dateStr)) return "";

        const date = moment(dateStr).format("MM-DD-YYYY");
        const time = moment(dateStr).format("HH:mm");



        return { date, time };
    } catch (err) {
        console.error("Invalid date or timezone", err);
        return { date: "", time: "" };
    }
};


export const base64ToBlob = async (base64, contentType) => {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
};

export const fmt = (dt) => {
    if (!dt) return "N/A"; // null, undefined, empty, 0

    const date = new Date(dt);
    if (isNaN(date.getTime())) return "N/A"; // invalid date

    return date.toLocaleString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

export function getFlightStatus(code) {
    switch (code) {
        case "A":
            return "Active (in flight)";
        case "C":
            return "Canceled";
        case "D":
            return "Diverted";
        case "DN":
            return "Data Needed / Not Available";
        case "L":
            return "Landed";
        case "NO":
            return "Not Operational";
        case "R":
            return "Redirected";
        case "S":
            return "Scheduled";
        case "U":
            return "Unknown";
        default:
            return code;
    }
}



export const calculateDelayDuration = (scheduled, actual) => {
    if (
        scheduled &&
        scheduled !== null &&
        scheduled !== undefined &&
        scheduled !== "" &&
        scheduled !== "+00:00" &&
        scheduled !== "" &&
        actual &&
        actual !== null &&
        actual !== undefined &&
        actual !== "" &&
        actual !== "+00:00" &&
        actual !== ""
    ) {
        try {



            const ms = moment.utc(actual.slice(0, 16)).diff(moment.utc(scheduled.slice(0, 16)));
            const mins = Math.floor(moment.duration(ms).asMinutes());
            return mins;

        } catch (error) {
            console.error("Error calculating delay duration:", error);
            return null;
        }
    }
    return null;
};



export const isValidTime = (timeStr) => {
    return timeStr && timeStr !== null && timeStr !== undefined && timeStr !== "" && timeStr !== "+00:00";
};


export const calculateDelay = (actualArrivalTime, originalArrivalTime) => {

    console.log(moment(actualArrivalTime).isValid())

    if (
        !moment(actualArrivalTime).isValid() ||
        !moment(originalArrivalTime).isValid()
    ) {
        return null;
    }


    console.log("Further")

    const duration = moment.duration(
        moment(actualArrivalTime).diff(moment(originalArrivalTime))
    );

    const isNegative = duration.asMilliseconds() < 0;
    const totalMinutes = Math.abs(Math.floor(duration.asMinutes()));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${isNegative ? "-" : ""}${String(hours).padStart(2, "0")}:${String(
        minutes
    ).padStart(2, "0")}`;
};

export const getDelayDuration = (duration) => {
    if (duration === null || duration === undefined || duration === "") return "N/A";

    const parsedStr = String(duration).match(/-?\d+/)?.[0];
    const parsed = parseInt(parsedStr, 10);
    if (Number.isNaN(parsed)) return "N/A";

    if (parsed === 0) return "0m";

    const sign = parsed < 0 ? "-" : "";
    const totalMinutes = Math.abs(parsed);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${sign}${minutes}m`;
    if (minutes === 0) return `${sign}${hours}hr`;
    return `${sign}${hours}hr ${minutes}m`;
};


export const validateDateFormat = (value) => {
    const ddmmyyyy = /^\d{2}-\d{2}-\d{4}$/; // dd-mm-yyyy
    const yyyymmdd = /^\d{4}-\d{2}-\d{2}$/; // yyyy-mm-dd

    if (ddmmyyyy.test(value) || yyyymmdd.test(value)) {
        return true;
    }

    return "Date must be in mm-dd-yyyy format";
};